import { CASHIER_MIRROR_MESSAGE_TYPES } from '../protocol/messageTypes.ts'
import { setMirrorPeerReady } from '../readiness/mirrorReadyState.ts'
import type {
  PlacingSlipErrorPayload,
  PrivateModePayload,
  SettingsUpdatePayload,
  SlipCheckPayload,
  SwitchGamePayload,
} from '../types/payloads.ts'
import type { CashierMirrorTransport } from '../transport/cashierMirrorTransport.ts'

export type CashierMirrorOperator = {
  checkMirrorStatus(): void
  onMirrorReady(handler: () => void): void
  slipCheck(slip: SlipCheckPayload): void
  operatorLogin(): void
  operatorLogout(): void
  switchGame(payload: SwitchGamePayload): void
  settingsUpdate(payload: SettingsUpdatePayload): void
  setPrivateMode(payload: PrivateModePayload): void
  placingSlip(): void
  placingSlipSuccess(): void
  placingSlipError(payload: PlacingSlipErrorPayload): void
}

export type CreateCashierMirrorOperatorOptions = {
  transport: CashierMirrorTransport
}

export function createCashierMirrorOperator(
  options: CreateCashierMirrorOperatorOptions,
): CashierMirrorOperator {
  const { transport } = options
  const mirrorReadyHandlers: Array<() => void> = []
  let sessionPublishEnabled = false

  transport.subscribe((message) => {
    if (message.type === CASHIER_MIRROR_MESSAGE_TYPES.announceMirrorReady) {
      sessionPublishEnabled = true
      setMirrorPeerReady(true)
      for (const handler of mirrorReadyHandlers) {
        handler()
      }
    }
  })

  const publishWhenReady = (type: string, payload?: unknown) => {
    if (!sessionPublishEnabled) {
      return
    }
    transport.publish(type, payload)
  }

  return {
    checkMirrorStatus() {
      transport.publish(CASHIER_MIRROR_MESSAGE_TYPES.checkMirrorStatus)
    },
    onMirrorReady(handler) {
      mirrorReadyHandlers.push(handler)
      if (sessionPublishEnabled) {
        handler()
      }
    },
    slipCheck(slip) {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.slipCheck, slip)
    },
    operatorLogin() {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.operatorLogin)
    },
    operatorLogout() {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.operatorLogout)
    },
    switchGame(payload) {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.switchGame, payload)
    },
    settingsUpdate(payload) {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.settingsUpdate, payload)
    },
    setPrivateMode(payload) {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.setPrivateMode, payload)
    },
    placingSlip() {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.placingSlip)
    },
    placingSlipSuccess() {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.placingSlipSuccess)
    },
    placingSlipError(payload) {
      publishWhenReady(CASHIER_MIRROR_MESSAGE_TYPES.placingSlipError, payload)
    },
  }
}
